import { Box, Stack, Typography } from '@mui/material'
import { FifaCircleFlag } from '@/components/CircleFlag'
import { Column, COLUMN_CELL_TYPE, Filter } from '@/components/table/base'
import { GOLF_PAR_COLOR, GOLF_TOTAL_COLUMN } from '@/constants/golfLeaderboard'
import { LeaderboardTableRow, Player } from '@/types/dde/golfLeaderboard.types'

const getParBadgeColor = (toPar: number): string => {
  if (toPar < 0) return GOLF_PAR_COLOR.NEGATIVE
  if (toPar > 0) return GOLF_PAR_COLOR.POSITIVE
  return GOLF_PAR_COLOR.NEUTRAL
}

const renderCountryAndNames = (_: unknown, row: LeaderboardTableRow) => {
  const { players } = row

  if (players.length === 1) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <FifaCircleFlag fifaCode={players[0].country} height={24} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {players[0].displayName}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack direction="column" spacing={0.5} paddingY={1}>
      {players.map((player) => (
        <Stack key={player.id} direction="row" alignItems="center" spacing={1}>
          <FifaCircleFlag fifaCode={player.country} height={20} />
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.813rem' }}>
            {player.displayName}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

const renderParBadge = (parDisplay: string, parValue: number) => {
  const backgroundColor = getParBadgeColor(parValue)

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
        color: 'common.white',
        borderRadius: 1,
        px: 0.5,
        py: 0.5,
        minWidth: 30,
        fontWeight: 600
      }}
    >
      {parDisplay}
    </Box>
  )
}

export const DDE_TABLE_COLUMNS: Column<LeaderboardTableRow>[] = [
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'position',
    label: 'Position',
    disableSorting: true,
    sx: { fontWeight: 600 }
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: '$countryAndNames',
    label: 'Player Name',
    sx: { minWidth: 200 },
    render: renderCountryAndNames
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: '$toPar',
    label: 'Par',
    render: (_cellValue, row: LeaderboardTableRow) => renderParBadge(row.toParDisplay, row.toPar)
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'hole',
    label: 'Hole'
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: '$toParToday',
    label: 'Today',
    render: (_cellValue, row: LeaderboardTableRow) =>
      renderParBadge(row.toParTodayDisplay, row.toParToday)
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'round1',
    label: 'R1'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'round2',
    label: 'R2'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'round3',
    label: 'R3'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'round4',
    label: 'R4'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'total',
    label: 'Total',
    sx: (theme) => ({
      fontWeight: GOLF_TOTAL_COLUMN.FONT_WEIGHT,
      '&.MuiTableCell-root': {
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100'
      }
    })
  }
]

export const DDE_TABLE_FILTERS: Filter<LeaderboardTableRow>[] = [
  {
    type: 'freeTextSearch',
    paramName: 'search',
    placeholder: 'Search players',
    filterOptions: (row, search) => {
      if (!search) return true
      const lowerCaseSearch = search.toLowerCase()
      return row.players.some(
        (player: Player) =>
          player.displayName.toLowerCase().includes(lowerCaseSearch) ||
          player.firstName.toLowerCase().includes(lowerCaseSearch) ||
          player.lastName.toLowerCase().includes(lowerCaseSearch) ||
          player.country.toLowerCase().includes(lowerCaseSearch)
      )
    }
  }
]
